import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Store, select } from "@ngrx/store";
import { EMPTY, Observable, Subject, combineLatest, from, of } from "rxjs";
import { catchError, concatMap, filter, map, mapTo, switchMap, takeUntil, toArray } from "rxjs/operators";
import { PaginatorDefaultOptions } from "src/app/common/components/paginator/paginator.component";
import { SLOT } from "src/app/common/enums/slot";
import { FeatureFlag } from "src/app/core/enums/feature-flag";
import { IProjectEntity, IProjectModuleEntity, MinMaxYears } from "src/app/core/interfaces/project-entity";
import { DynamicSnackBar } from "src/app/core/material/snack-bar/snack-bar";
import { FeatureFlagService } from "src/app/core/services/feature-flag.service";
import { GridScenariosDataService } from "src/app/core/services/grid-scenarios-data.service";
import { ProjectConfigurationService } from "src/app/core/services/project-configuration.service";
import { ConfigAreaConfirmDialogComponent } from "src/app/features/configuration-area/common/dialogs/confirm/confirm.component";
import { ConfigAreaSettingsDialogComponent } from "src/app/features/configuration-area/common/dialogs/settings/settings.component";
import { CreateMunicipalityDialogService } from "src/app/features/configuration-area/services/create-municipality-dialog.service";
import { setEntities, setMinMaxYears } from "src/app/ngrx/actions/app.actions";
import { RootState } from "src/app/ngrx/root-reducers";
import { selectEntities } from "src/app/ngrx/selectors/app.selectors";
import { getActivatedDivisions, ModuleDivisionState } from "src/app/shared/utils/module-activation";
import { TableAction } from "src/app/slots/three-year-plan/common/components/table/table.interfaces";
import {
  concessionsModulesCheckboxes,
  concessionsStatesCheckboxes,
  configurationAreaConcessionsColumns
} from "../modules-tab.constants";
import { TableRow } from "../modules-tab.interfaces";

@Component({
  selector: "app-modules-tab",
  templateUrl: "./modules-tab.component.html",
  styleUrls: ["./modules-tab.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    class: "ca-modules-tab"
  }
})
export class CAModulesTabComponent implements OnInit, OnDestroy {
  public readonly pagingConfig: PaginatorDefaultOptions = {
    pageSize: 20,
    pageSizeOptions: [20, 50, 100, 1000],
    showFirstLastButtons: true
  };
  public readonly vm$ = this.store.pipe(
    select(selectEntities),
    map((entities = []) =>
      entities.map((entity) => ({
        name: entity.entity.name,
        ags: entity.entity.ags,
        concession: entity.config.concession ? "ja" : "nein",
        ...this.getModules(entity.moduleComposition),
        entity
      }))
    ),
    map((list) => ({
      modulesCheckboxes: concessionsModulesCheckboxes,
      statesCheckboxes: concessionsStatesCheckboxes,
      columns: configurationAreaConcessionsColumns,
      list
    }))
  );
  public readonly isCreateMunicipalityEnabled = this.featureFlagService.isEnabled(
    FeatureFlag.ConfigAreaCreateMunicipality
  );
  public checkboxesForm: FormGroup;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store<RootState>,
    private readonly matDialog: MatDialog,
    private readonly fb: FormBuilder,
    private readonly projectConfigurationService: ProjectConfigurationService,
    private readonly snackbar: DynamicSnackBar,
    private readonly featureFlagService: FeatureFlagService,
    private readonly createMunicipalityDialogService: CreateMunicipalityDialogService,
    private readonly gridScenariosDataService: GridScenariosDataService
  ) {}

  public ngOnInit(): void {
    this.checkboxesForm = this.createCheckboxesForm();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public trackById(_: number, { id }: { id: string }): string {
    return id;
  }

  public openDialog(item: TableRow): void {
    const config = {
      width: "640px",
      panelClass: "no-padding",
      autoFocus: false
    };
    this.matDialog
      .open(ConfigAreaSettingsDialogComponent, {
        ...config,
        data: { entity: item.entity }
      })
      .afterClosed()
      .pipe(
        switchMap((data) =>
          data
            ? this.matDialog
                .open(ConfigAreaConfirmDialogComponent, config)
                .afterClosed()
                .pipe(filter(Boolean), mapTo(data))
            : EMPTY
        ),
        switchMap((data: any) => {
          if (data.municipality.coatOfArms instanceof FormData) {
            return this.projectConfigurationService
              .updateEntityEmblem(data.municipality.entityId, data.municipality.coatOfArms)
              .pipe(
                map(() => {
                  this.snackbar.open(
                    "CONFIGURATION_AREA.DIALOGS.CONFIRM.SAVE_SUCCESS",
                    undefined,
                    undefined,
                    "success",
                    false
                  );
                  return data;
                }),
                catchError(() => {
                  this.snackbar.open(
                    "CONFIGURATION_AREA.DIALOGS.CONFIRM.SAVE_ERROR",
                    undefined,
                    undefined,
                    "error",
                    false
                  );
                  return of(data);
                })
              );
          } else {
            return of(data);
          }
        }),
        switchMap((data) =>
          this.projectConfigurationService
            .updateEntity(item.entity.entityId, data)
            .pipe(switchMap(() => this.createDefaultScenarios(item.entity, data.modules.items)))
        ),
        switchMap(() => this.reloadEntities()),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => this.applyEntities(result));
  }

  public openCreateMunicipalityDialog(): void {
    this.createMunicipalityDialogService
      .open()
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.reloadEntities()),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => this.applyEntities(result));
  }

  public onTableEvent(event: TableAction<TableRow>): void {
    if (event.type === "click") {
      this.openDialog(event.row);
    }
  }

  private reloadEntities(): Observable<[Array<IProjectEntity>, MinMaxYears]> {
    return combineLatest([
      this.projectConfigurationService.getEntities({ filter: true }),
      this.projectConfigurationService.getYears()
    ]);
  }

  private applyEntities([entities, years]: [Array<IProjectEntity>, MinMaxYears]): void {
    this.store.dispatch(setEntities({ payload: entities }));
    this.store.dispatch(setMinMaxYears({ payload: years }));
  }

  /** Starts the default grid scenario calculation for every division just activated in the grid-scenarios module. */
  private createDefaultScenarios(
    entity: IProjectEntity,
    modules: Array<ModuleDivisionState & { moduleId: string }>
  ): Observable<void> {
    if (!this.featureFlagService.isEnabled(FeatureFlag.NEuSAutoStartNetzeBwScenario)) {
      return of(undefined);
    }

    const divisions = getActivatedDivisions(
      entity.moduleComposition.find(({ id }) => id === SLOT.GRID_SCENARIOS),
      modules.find(({ moduleId }) => moduleId === SLOT.GRID_SCENARIOS)
    );

    if (!divisions.length) {
      return of(undefined);
    }

    return from(divisions).pipe(
      concatMap((division) => this.gridScenariosDataService.createDefaultScenario(division, entity.entity.ags)),
      toArray(),
      map((results) => {
        if (results.some((result) => result instanceof Error)) {
          this.snackbar.open("CONFIGURATION_AREA.DIALOGS.CONFIRM.SAVE_ERROR", undefined, undefined, "error", false);
        }
      })
    );
  }

  private createCheckboxesForm(): FormGroup {
    const form = this.fb.group({
      modules: this.fb.group(concessionsModulesCheckboxes.reduce((acc, { id }) => ({ ...acc, [id]: true }), {})),
      states: this.fb.group(concessionsStatesCheckboxes.reduce((acc, { id }) => ({ ...acc, [id]: true }), {}))
    });
    // not available now
    form.disable();

    return form;
  }

  private getModules(moduleComposition: Array<IProjectModuleEntity>): {
    active: boolean;
    divisions: string;
  } {
    return moduleComposition.reduce(
      (acc, { id, active, config }) => {
        const divisions = [];

        if (config.division.electricity) {
          divisions.push("S");
        }

        if (config.division.gas) {
          divisions.push("G");
        }

        return {
          ...acc,
          [id]: { active, divisions: divisions.join(", ") }
        };
      },
      {} as {
        active: boolean;
        divisions: string;
      }
    );
  }
}
